import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  DollarSign,
  TrendingDown,
  Boxes,
  ChevronDown,
  ChevronRight,
  Edit3,
  Save,
  X,
  User,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupplyInventory = ({ user }) => {
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [expandedItems, setExpandedItems] = useState({});

  const isAdmin = user?.is_admin || user?.role === "supervisor";

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/supplies/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(res.data);
      setBudgetInput(res.data.available_funds.toString());
    } catch (error) {
      toast.error("Failed to load supply inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleUpdateBudget = async () => {
    try {
      const token = localStorage.getItem("token");
      const newBudget = parseFloat(budgetInput) || 0;
      await axios.put(`${API}/budget`, 
        { available_funds: newBudget },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Budget updated successfully");
      setEditingBudget(false);
      fetchInventory();
    } catch (error) {
      toast.error("Failed to update budget");
    }
  };

  const toggleExpanded = (supplyName) => {
    setExpandedItems(prev => ({
      ...prev,
      [supplyName]: !prev[supplyName]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading">Supply Inventory</h1>
          <p className="text-muted-foreground">Track budget and available supply overages from programs</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Available Funds
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editingBudget && isAdmin ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                  className="h-8 w-28"
                  data-testid="budget-input"
                />
                <Button size="sm" variant="ghost" onClick={handleUpdateBudget} className="h-8 w-8 p-0">
                  <Save className="h-4 w-4 text-green-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingBudget(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-green-700" data-testid="available-funds">
                  {formatCurrency(inventory?.available_funds || 0)}
                </p>
                {isAdmin && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setEditingBudget(true)}
                    className="h-8 w-8 p-0"
                    data-testid="edit-budget-btn"
                  >
                    <Edit3 className="h-4 w-4 text-gray-500" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Committed Funds
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-amber-700" data-testid="committed-funds">
              {formatCurrency(inventory?.committed_funds || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Locked in approved programs</p>
          </CardContent>
        </Card>

        <Card className={`border-blue-200 ${(inventory?.remaining_funds || 0) < 0 ? 'bg-red-50/30 border-red-200' : 'bg-blue-50/30'}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${(inventory?.remaining_funds || 0) < 0 ? 'text-red-700' : 'text-blue-700'}`}>
              <DollarSign className="h-4 w-4" />
              Remaining Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${(inventory?.remaining_funds || 0) < 0 ? 'text-red-700' : 'text-blue-700'}`} data-testid="remaining-funds">
              {formatCurrency(inventory?.remaining_funds || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Available for new programs</p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
              <Boxes className="h-4 w-4" />
              Supply Overages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-purple-700" data-testid="total-overages">
              {inventory?.total_overage_items || 0} items
            </p>
            <p className="text-xs text-muted-foreground mt-1">Available from past programs</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-purple-600" />
            Available Supply Overages
          </CardTitle>
          <CardDescription>
            Leftover supplies from previous programs that can be reused
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!inventory?.overages?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <Boxes className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No supply overages available</p>
              <p className="text-sm">Overages appear when programs purchase more supplies than needed</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Supply Item</TableHead>
                  <TableHead className="text-center">Available Qty</TableHead>
                  <TableHead className="text-center">Sources</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventory.overages.map((overage) => (
                  <Collapsible 
                    key={overage.supply_name} 
                    open={expandedItems[overage.supply_name]}
                    onOpenChange={() => toggleExpanded(overage.supply_name)}
                    asChild
                  >
                    <>
                      <TableRow className="hover:bg-muted/50" data-testid={`overage-row-${overage.supply_name}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-purple-500" />
                            {overage.supply_name}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            {overage.total_overage} available
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {overage.sources.length} program{overage.sources.length !== 1 ? 's' : ''}
                        </TableCell>
                        <TableCell>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              {expandedItems[overage.supply_name] ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow className="bg-purple-50/30">
                          <TableCell colSpan={4} className="py-0">
                            <div className="py-3 px-4 space-y-2">
                              <p className="text-xs font-medium text-muted-foreground mb-2">Source Breakdown:</p>
                              {overage.sources.map((source, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex items-center justify-between p-2 rounded bg-white border border-purple-100"
                                >
                                  <div className="flex items-center gap-3">
                                    <FileText className="h-4 w-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm font-medium">{source.proposal_name}</p>
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        {source.created_by_name}
                                      </p>
                                    </div>
                                  </div>
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700">
                                    +{source.qty_overage}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplyInventory;
